import dynamic from 'next/dynamic';
import Images from 'next/image';
import LazyLoad from 'react-lazyload';
import { APP_IMG } from 'src/constants';

const ContentLoader = dynamic(import('src/components/content-loader'));

type ImageProps = {
  alt?: string;
  title?: string;
  image?: string;
  ignoreLazy?: boolean;
  horizontal?: boolean;
  exLazy?: string;
  external?: boolean;
  type?: 'FEATURE_IMAGE' | 'COVER_IMAGE' | 'CARD_LARGE' | 'CARD_MEDIUM' | 'CARD_SMALL' | 'NEXT_IMAGE' | string;
  priority?: boolean;
  preload?: boolean;
  sizes?: string;
  decorative?: boolean;
};

const Image = ({ alt, title, image, ignoreLazy, horizontal = false, exLazy = '', external = false, type, priority = false, preload = false, sizes, decorative = false }: ImageProps) => {
  const loadingAttr: 'eager' | 'lazy' = priority ? 'eager' : 'lazy';
  const fetchPriorityAttr: 'high' | 'auto' = priority ? 'high' : 'auto';
  const decodingAttr: 'sync' | 'async' = priority ? 'sync' : 'async';

  const altText = decorative ? '' : (alt ?? title ?? 'image');
  const titleText = decorative ? undefined : (title ?? alt ?? 'image');

  let elem = (
    <div className='resolution-image'>
      <img
        src={`${APP_IMG}/default.jpg`}
        alt={altText}
        title={titleText}
        loading={loadingAttr}
        //@ts-ignore - fetchpriority ยังไม่อยู่ใน React types
        fetchpriority={fetchPriorityAttr}
        decoding={decodingAttr}
        width='100%'
        height='100%'
      />
    </div>
  );

  if (image) {
    let imgUrl = image;
    if (!external) {
      imgUrl = `${APP_IMG}${image}`;
    }

    //ใช้ React.ImgHTMLAttributes<HTMLImageElement> เป็น type ตรงๆ
    const imgCommonProps: React.ImgHTMLAttributes<HTMLImageElement> & { fetchpriority?: string } = {
      alt: altText,
      loading: loadingAttr,
      className: exLazy,
      width: '100%',
      height: '100%',
      decoding: decodingAttr,
      fetchpriority: fetchPriorityAttr,
      ...(decorative ? { 'aria-hidden': true } : {}),
      ...(sizes ? { sizes } : {})
    };

    switch (type) {
      case 'FEATURE_IMAGE':
        return (elem = (
          <>
            {priority && preload && (
              <link
                rel='preload'
                as='image'
                href={`${imgUrl}?x-image-process=style/lg-webp`}
                //@ts-ignore
                imagesrcset={`${imgUrl}?x-image-process=style/MD-webp 767w, ${imgUrl}?x-image-process=style/lg-webp 1440w`}
                //@ts-ignore
                imagesizes={sizes || '100vw'}
                //@ts-ignore
                fetchpriority='high'
                type='image/webp'
              />
            )}
            <picture className='resolution-image'>
              <source srcSet={`${imgUrl}?x-image-process=style/MD-webp`} media={'(max-width: 767px)'} type='image/webp' />
              <source srcSet={`${imgUrl}?x-image-process=style/MD`} media={'(max-width: 767px)'} type='image/jpeg' />
              <source srcSet={`${imgUrl}?x-image-process=style/lg-webp`} media={'(max-width: 1440px)'} type='image/webp' />
              <source srcSet={`${imgUrl}?x-image-process=style/lg`} media={'(max-width: 1440px)'} type='image/jpeg' />
              <img src={`${imgUrl}?x-image-process=style/lg`} {...imgCommonProps} />
            </picture>
          </>
        ));
      case 'COVER_IMAGE':
        return (elem = (
          <>
            {priority && preload && (
              <link
                rel='preload'
                as='image'
                href={`${imgUrl}?x-image-process=style/XL-webp`}
                //@ts-ignore
                imagesrcset={`${imgUrl}?x-image-process=style/LG-webp 767w, ${imgUrl}?x-image-process=style/XL-webp 1920w`}
                //@ts-ignore
                imagesizes={sizes || '100vw'}
                //@ts-ignore
                fetchpriority='high'
                type='image/webp'
              />
            )}
            <picture className='resolution-image'>
              <source srcSet={`${imgUrl}?x-image-process=style/LG-webp`} media={'(max-width: 767px)'} type='image/webp' />
              <source srcSet={`${imgUrl}?x-image-process=style/LG`} media={'(max-width: 767px)'} type='image/jpeg' />
              <source srcSet={`${imgUrl}?x-image-process=style/XL-webp`} type='image/webp' />
              <img src={`${imgUrl}?x-image-process=style/XL`} {...imgCommonProps} />
            </picture>
          </>
        ));
      case 'CARD_LARGE':
        return (elem = (
          <>
            {priority && preload && (
              <link
                rel='preload'
                as='image'
                href={`${imgUrl}?x-image-process=style/LG-webp`}
                //@ts-ignore
                fetchpriority='high'
                type='image/webp'
              />
            )}
            <picture className='resolution-image'>
              <source srcSet={`${imgUrl}?x-image-process=style/lg-webp`} type='image/webp' />
              <img src={`${imgUrl}?x-image-process=style/lg`} {...imgCommonProps} />
            </picture>
          </>
        ));
      case 'CARD_MEDIUM':
        return (elem = (
          <>
            {priority && preload && (
              <link
                rel='preload'
                as='image'
                href={`${imgUrl}?x-image-process=style/MD-webp`}
                //@ts-ignore
                fetchpriority='high'
                type='image/webp'
              />
            )}
            <picture className='resolution-image'>
              <source srcSet={`${imgUrl}?x-image-process=style/MD-webp`} type='image/webp' />
              <img src={`${imgUrl}?x-image-process=style/MD`} {...imgCommonProps} />
            </picture>
          </>
        ));
      case 'CARD_SMALL':
        return (elem = (
          <>
            {priority && preload && (
              <link
                rel='preload'
                as='image'
                href={`${imgUrl}?x-image-process=style/XS-webp`}
                //@ts-ignore
                imagesrcset={`${imgUrl}?x-image-process=style/SS-webp 767w, ${imgUrl}?x-image-process=style/XS-webp 1440w`}
                //@ts-ignore
                imagesizes={sizes || '100vw'}
                //@ts-ignore
                fetchpriority='high'
                type='image/webp'
              />
            )}
            <picture className='resolution-image'>
              <source srcSet={`${imgUrl}?x-image-process=style/XS-webp`} media={'(max-width: 767px)'} type='image/webp' />
              <source srcSet={`${imgUrl}?x-image-process=style/XS`} media={'(max-width: 767px)'} type='image/jpeg' />
              <source srcSet={`${imgUrl}?x-image-process=style/XS-webp`} type='image/webp' />
              <img src={`${imgUrl}?x-image-process=style/XS`} {...imgCommonProps} />
            </picture>
          </>
        ));
      case 'CARD_TINY':
        return (elem = (
          <>
            {priority && preload && (
              <link
                rel='preload'
                as='image'
                href={`${imgUrl}?x-image-process=style/XS-webp`}
                // @ts-ignore
                imagesrcset={`${imgUrl}?x-image-process=style/SS-webp 767w, ${imgUrl}?x-image-process=style/XS-webp 1440w`}
                // @ts-ignore
                imagesizes={sizes || '100vw'}
                // @ts-ignore
                fetchpriority='high'
                type='image/webp'
              />
            )}
            <picture className='resolution-image'>
              <source srcSet={`${imgUrl}?x-image-process=style/SS-webp`} media={'(max-width: 767px)'} type='image/webp' />
              <source srcSet={`${imgUrl}?x-image-process=style/SS`} media={'(max-width: 767px)'} type='image/jpeg' />
              <source srcSet={`${imgUrl}?x-image-process=style/SS-webp`} type='image/webp' />
              <img src={`${imgUrl}?x-image-process=style/SS`} {...imgCommonProps} />
            </picture>
          </>
        ));
      case 'NEXT_IMAGE':
        return (elem = (
          <>
            <picture className='resolution-image'>
              <Images src={`${imgUrl}`} alt={altText} priority={priority} layout='fill' quality={priority ? 90 : 100} />
            </picture>
          </>
        ));
      default:
        return (elem = (
          <>
            {priority && preload && (
              <link
                rel='preload'
                as='image'
                href={`${imgUrl}?x-image-process=style/lg-webp`}
                //@ts-ignore
                imagesrcset={`${imgUrl}?x-image-process=style/lg-webp 1440w`}
                //@ts-ignore
                imagesizes={sizes || '100vw'}
                //@ts-ignore
                fetchpriority='high'
                type='image/webp'
              />
            )}
            <picture className='resolution-image'>
              <source srcSet={`${imgUrl}?x-image-process=style/lg-webp`} media={'(max-width: 1440px)'} type='image/webp' />
              <source srcSet={`${imgUrl}?x-image-process=style/lg-webp`} media={'(max-width: 1440px)'} type='image/jpeg' />
              <img src={`${imgUrl}?x-image-process=style/lg-webp`} {...imgCommonProps} />
            </picture>
          </>
        ));
    }
  }

  if (priority || ignoreLazy) return elem;

  return <LazyLoad placeholder={<ContentLoader hasText={false} horizontal={horizontal} />}>{elem}</LazyLoad>;
};

export default Image;
